import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["modalId"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { cloneElement, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Backdrop } from '@mui/material';
import { getModalDataSelector, getModalOpenStateSelector } from '@store/modals/modals.selectors';
import { ModalsService } from '@services';
import { modalVariants } from '@components/modal/modal.variants';
import { StyledDialog, StyledPaper } from './modal.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

/**
 * Modal component.
 *
 * @author Mikhail Siutsou
 * @function ModalComponent
 * @category components
 * @param {string} modalId modal id.
 * @return {FC} modal component.
 */
var ModalComponent = function ModalComponent(_ref) {
  var _childrenElement$prop, _childrenElement$prop2;

  var modalId = _ref.modalId,
      pr = _objectWithoutProperties(_ref, _excluded);

  var isOpen = useSelector(getModalOpenStateSelector(modalId));
  var data = useSelector(getModalDataSelector(modalId));
  var component = modalVariants[modalId];
  /**
   * Reject modal.
   *
   * @author Mikhail Siutsou
   * @return {void}
   */

  var rejectModal = useCallback(function () {
    ModalsService.closeWithAnswer(modalId, {
      rejected: true
    });
  }, [modalId]);
  /**
   * Accept modal.
   *
   * @author Mikhail Siutsou
   * @return {void}
   */

  var acceptModal = useCallback(function () {
    ModalsService.closeWithAnswer(modalId, {
      accepted: true
    });
  }, [modalId]);
  /**
   * Close modal.
   *
   * @author Mikhail Siutsou
   * @return {void}
   */

  var handleClose = useCallback(function () {
    rejectModal();
  }, [rejectModal]);
  var initialChildProps = useMemo(function () {
    return {
      handleClose: handleClose,
      acceptModal: acceptModal,
      rejectModal: rejectModal,
      modalId: modalId,
      data: data
    };
  }, [acceptModal, handleClose, modalId, rejectModal, data]);
  var modalChild = component ? component(initialChildProps) : null;
  var children = useMemo(function () {
    return modalChild && /*#__PURE__*/cloneElement(modalChild, initialChildProps);
  }, [initialChildProps, modalChild]);
  var childrenElement = (children === null || children === void 0 ? void 0 : children.props) && children.props.children;
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */

  var maxWidth = childrenElement && ((_childrenElement$prop = childrenElement.props) === null || _childrenElement$prop === void 0 ? void 0 : _childrenElement$prop.maxWidth);
  var fullWidth = childrenElement && ((_childrenElement$prop2 = childrenElement.props) === null || _childrenElement$prop2 === void 0 ? void 0 : _childrenElement$prop2.fullWidth);
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */

  var modalProps = children !== null && children !== void 0 && children.props ? {
    maxWidth: children.props.maxWidth || maxWidth,
    fullWidth: children.props.fullWidth || fullWidth
  } : {};
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(StyledDialog, _objectSpread(_objectSpread({
      "aria-labelledby": modalId,
      open: isOpen,
      onClose: handleClose,
      closeAfterTransition: true,
      BackdropComponent: Backdrop,
      BackdropProps: {
        timeout: 500
      },
      scroll: "body",
      PaperComponent: StyledPaper,
      PaperProps: {
        elevation: 0
      }
    }, modalProps), {}, {
      children: children
    }))
  });
};

export { ModalComponent };